import type { PresenceUser } from '../features/presence/PresenceStack';

/**
 * The real-time presence socket — "who else is here, and what are they looking at".
 *
 * ONE ENTRY PER SOCKET, not per person. Somebody with two tabs open is two entries, and
 * collapsing that is the consumer's decision because which of their tabs to show depends on
 * what is being shown (`PresenceStack` does it for a header, the map does it per node).
 *
 * THE ROSTER IS OWNED HERE and read by everything that draws an avatar. It is emptied on
 * close as well as filled on message: a socket that dropped means we no longer know who is
 * here, and leaving the last roster on screen would show people who may have left hours ago.
 *
 * A SECOND MESSAGE TYPE RIDES THE SAME SOCKET (V0.34): `{type:'mapChanged', dataVersion}`,
 * which the server sends after any write that really moved shared map content. It is here
 * rather than on its own connection because the connection already exists, and it is a
 * dedicated type rather than a field on the presence snapshot because that one is sent on
 * every roster change and a reader could not tell which fact caused it. The module owns the
 * MECHANISM and boot owns the ANSWER, exactly as with `loc` and `onRoster`: what to do about a
 * version this socket has never heard of is not a socket's decision.
 *
 * WHERE AM I is not this module's business. `connect()` is handed a function that answers it,
 * because the answer is assembled from the doc view, the open popup and the edit toggle —
 * three things that belong to the app, not to a socket. The 150 ms coalescing on `sendLoc()`
 * matters: opening a document changes several of those in the same tick, and without it the
 * server would receive three positions for one action.
 *
 * RECONNECTION backs off from 1 s to a cap of 15 s, and only while `wantOpen` — a deliberate
 * disconnect (signing out, losing the role) must not be undone by a timer that was already in
 * flight.
 *
 * A DELIBERATE DISCONNECT DETACHES THE SOCKET'S HANDLERS before closing it, and that is not
 * tidiness. `close()` is asynchronous: the browser fires `onclose` a turn or more later, and
 * that handler empties the roster. So "disconnect, then set a roster" — which is what signing
 * out and back in does, and what `e2e/21-topbar-right.spec.ts` does to show nine people —
 * used to be a race in which a late `onclose` wiped what had just been set. Clearing the
 * roster synchronously here is what this function does; detaching the handlers is what stops
 * anything else undoing it afterwards.
 */

export type PresenceLoc = {
  view: 'map' | 'doc';
  nodeId: string | null;
  docId: string | null;
  mode: 'view' | 'edit';
};

const RECONNECT_MIN = 1000;
const RECONNECT_MAX = 15_000;
const LOC_COALESCE_MS = 150;

let ws: WebSocket | null = null;
let roster: PresenceUser[] = [];
let wantOpen = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = RECONNECT_MIN;
let locTimer: ReturnType<typeof setTimeout> | null = null;

let getLoc: () => PresenceLoc = () => ({ view: 'map', nodeId: null, docId: null, mode: 'view' });
let onRoster: () => void = () => {};
/** May we still be connected at all? Re-asked before every reconnect attempt. */
let stillAllowed: () => boolean = () => false;
/** "The shared map is at version V." Defaulted to a no-op so a socket without a listener —
 *  a unit run, or a boot that has not wired it yet — costs nothing rather than throwing. */
let onMapChanged: (dataVersion: number) => void = () => {};
/** "The socket is up." Same defaulting, same reason. */
let onOpen: () => void = () => {};

export function configurePresence(opts: {
  loc: () => PresenceLoc;
  onRoster: () => void;
  allowed: () => boolean;
  onMapChanged?: (dataVersion: number) => void;
  /**
   * Told every time the socket opens — including a RECONNECT (V0.48).
   *
   * A reconnect is evidence about the server, not just about this socket: the connection went
   * away and has come back, which is exactly what a restart or a restore looks like from here.
   * The server closes every socket at the end of a restore (code 4001) precisely so this
   * fires, and the app can re-ask for the map without the reader touching anything. Before
   * this, a tab left open through a restore sat on the old map until its next focus.
   */
  onOpen?: () => void;
}): void {
  getLoc = opts.loc;
  onRoster = opts.onRoster;
  stillAllowed = opts.allowed;
  if (opts.onMapChanged) onMapChanged = opts.onMapChanged;
  if (opts.onOpen) onOpen = opts.onOpen;
}

/** The live roster. Replaced, never mutated, so a consumer can compare by identity. */
export function getRoster(): readonly PresenceUser[] {
  return roster;
}

/**
 * Replace the roster from outside the socket.
 *
 * Exists for `e2e/21-topbar-right.spec.ts`, which injects a synthetic roster to check the
 * "+N" collapse at nine people — a case the real map cannot be made to produce on demand.
 * It is the same array every consumer reads, so what the spec sets is what the app draws.
 */
export function setRoster(next: readonly PresenceUser[]): void {
  roster = Array.isArray(next) ? [...next] : [];
  onRoster();
}

export function connectPresence(): void {
  wantOpen = true;
  if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) return;
  let socket: WebSocket;
  try {
    socket = new WebSocket(
      `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/api/presence`,
    );
  } catch {
    return;
  }
  ws = socket;
  socket.onopen = () => {
    reconnectDelay = RECONNECT_MIN;
    sendLoc();
    onOpen();
  };
  socket.onmessage = (ev: MessageEvent) => {
    let msg: { type?: string; users?: PresenceUser[]; dataVersion?: unknown };
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    if (!msg) return;
    if (msg.type === 'presence') {
      roster = Array.isArray(msg.users) ? msg.users : [];
      onRoster();
      return;
    }
    // Passed on verbatim, including the version this tab itself just caused: the server does
    // not exclude the originator (their OTHER tabs are just as stale), and only the listener
    // can tell its own write's echo from somebody else's edit.
    if (msg.type === 'mapChanged' && typeof msg.dataVersion === 'number') {
      onMapChanged(msg.dataVersion);
    }
  };
  socket.onclose = () => {
    if (ws === socket) ws = null;
    roster = [];
    onRoster();
    if (!wantOpen) return;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      if (wantOpen && stillAllowed()) connectPresence();
    }, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX);
  };
  socket.onerror = () => {
    try {
      socket.close();
    } catch {
      /* already closing */
    }
  };
}

export function disconnectPresence(): void {
  wantOpen = false;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  if (ws) {
    const socket = ws;
    ws = null;
    // Detach first: the close is asynchronous, and this socket's `onclose` would otherwise
    // land after we have already cleared the roster — and after anything set since.
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    try {
      socket.close();
    } catch {
      /* already closed */
    }
  }
  roster = [];
  onRoster();
}

/** Tell the server where I am. Coalesced: one action can move several of the inputs. */
export function sendLoc(): void {
  if (locTimer) clearTimeout(locTimer);
  locTimer = setTimeout(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: 'loc', ...getLoc() }));
    } catch {
      /* the socket went away between the check and the send */
    }
  }, LOC_COALESCE_MS);
}
